/* eslint-disable max-lines */

// Constants
export const DEFAULT_TOLERANCE = 32;
export const RGBA_CHANNELS = 4;
export const TRANSPARENT_ALPHA = 0;
export const OPAQUE_ALPHA = 255;
export const MASK_PIXEL_ON = 1;
export const MASK_PIXEL_OFF = 0;

// Types
export type RgbaColor = {
  r: number;
  g: number;
  b: number;
  a: number;
};

export type Point = {
  x: number;
  y: number;
};

export type ImageDimensions = {
  width: number;
  height: number;
};

export type FloodFillParams = {
  startX: number;
  startY: number;
  tolerance: number;
};

export type PixelChangeMap = Map<string, RgbaColor>;

export type FloodFillMaskResult = {
  mask: Uint8Array;
  width: number;
  height: number;
  count: number;
};

// Utility functions
export function getPixelIndex(x: number, y: number, width: number): number {
  return (y * width + x) * RGBA_CHANNELS;
}

export function getArrayIndex(x: number, y: number, width: number): number {
  return y * width + x;
}

export function createPointKey(point: Point): string {
  return `${point.x},${point.y}`;
}

export function parsePointKey(key: string): Point {
  const [xStr, yStr] = key.split(",");
  const x = Number(xStr);
  const y = Number(yStr);
  return { x, y };
}

export function isPointInBounds(point: Point, dimensions: ImageDimensions): boolean {
  return point.x >= 0 && point.x < dimensions.width && point.y >= 0 && point.y < dimensions.height;
}

export function isPixelTransparent(color: RgbaColor): boolean {
  return color.a === TRANSPARENT_ALPHA;
}

export function createTransparentColor(baseColor: RgbaColor): RgbaColor {
  return {
    r: baseColor.r,
    g: baseColor.g,
    b: baseColor.b,
    a: TRANSPARENT_ALPHA
  };
}

// Core pixel operations
export function getPixelColor(
  data: Uint8ClampedArray,
  params: { point: Point; width: number }
): RgbaColor {
  const { point, width } = params;
  const index = getPixelIndex(point.x, point.y, width);
  return {
    r: data[index] ?? 0,
    g: data[index + 1] ?? 0,
    b: data[index + 2] ?? 0,
    a: data[index + 3] ?? 0
  };
}

export function setPixelColor(
  data: Uint8ClampedArray,
  params: { point: Point; width: number; color: RgbaColor }
): void {
  const { point, width, color } = params;
  const index = getPixelIndex(point.x, point.y, width);
  data[index] = color.r;
  data[index + 1] = color.g;
  data[index + 2] = color.b;
  data[index + 3] = color.a;
}

export function colorsMatch(color1: RgbaColor, color2: RgbaColor, tolerance: number): boolean {
  return (
    Math.abs(color1.r - color2.r) <= tolerance &&
    Math.abs(color1.g - color2.g) <= tolerance &&
    Math.abs(color1.b - color2.b) <= tolerance
  );
}

// Flood fill algorithm helpers
function getAdjacentPoints(point: Point): Point[] {
  return [
    { x: point.x + 1, y: point.y },
    { x: point.x - 1, y: point.y },
    { x: point.x, y: point.y + 1 },
    { x: point.x, y: point.y - 1 }
  ];
}

function validateFloodFillParams(params: FloodFillParams, dimensions: ImageDimensions): boolean {
  const startPoint = { x: params.startX, y: params.startY };
  return isPointInBounds(startPoint, dimensions);
}

// Main flood fill functions
export function floodFillEraseWithDiff(
  imgData: ImageData,
  params: FloodFillParams
): PixelChangeMap {
  const { startX, startY, tolerance } = params;
  const { width, height, data } = imgData;
  const dimensions = { width, height };
  const changes: PixelChangeMap = new Map();

  if (!validateFloodFillParams(params, dimensions)) {
    return changes;
  }

  const startPoint = { x: startX, y: startY };
  const targetColor = getPixelColor(data, { point: startPoint, width });

  if (isPixelTransparent(targetColor)) {
    return changes;
  }

  const visited = new Set<string>();
  const stack: Point[] = [startPoint];

  while (stack.length > 0) {
    const currentPoint = stack.pop() as Point;
    const pointKey = createPointKey(currentPoint);

    if (!visited.has(pointKey) && isPointInBounds(currentPoint, dimensions)) {
      visited.add(pointKey);
      const currentColor = getPixelColor(data, { point: currentPoint, width });

      if (!isPixelTransparent(currentColor) && colorsMatch(currentColor, targetColor, tolerance)) {
        if (!changes.has(pointKey)) {
          changes.set(pointKey, currentColor);
        }

        const transparentColor = createTransparentColor(currentColor);
        setPixelColor(data, { point: currentPoint, width, color: transparentColor });

        const adjacentPoints = getAdjacentPoints(currentPoint);
        stack.push(...adjacentPoints);
      }
    }
  }

  return changes;
}

export function floodFillMask(imgData: ImageData, params: FloodFillParams): FloodFillMaskResult {
  const { startX, startY, tolerance } = params;
  const { width, height, data } = imgData;
  const dimensions = { width, height };
  const mask = new Uint8Array(width * height);

  if (!validateFloodFillParams(params, dimensions)) {
    return { mask, width, height, count: 0 };
  }

  const startPoint = { x: startX, y: startY };
  const targetColor = getPixelColor(data, { point: startPoint, width });

  if (isPixelTransparent(targetColor)) {
    return { mask, width, height, count: 0 };
  }

  const visited = new Uint8Array(width * height);
  const stack: Point[] = [startPoint];
  let count = 0;

  while (stack.length > 0) {
    const currentPoint = stack.pop() as Point;

    if (isPointInBounds(currentPoint, dimensions)) {
      const arrayIndex = getArrayIndex(currentPoint.x, currentPoint.y, width);

      if (!visited[arrayIndex]) {
        visited[arrayIndex] = MASK_PIXEL_ON;
        const currentColor = getPixelColor(data, { point: currentPoint, width });

        if (
          !isPixelTransparent(currentColor) &&
          colorsMatch(currentColor, targetColor, tolerance)
        ) {
          if (!mask[arrayIndex]) {
            mask[arrayIndex] = MASK_PIXEL_ON;
            count += 1;
          }

          const adjacentPoints = getAdjacentPoints(currentPoint);
          stack.push(...adjacentPoints);
        }
      }
    }
  }

  return { mask, width, height, count };
}

// Change application functions
export function applyChanges(imgData: ImageData, changes: PixelChangeMap): void {
  const { width, data } = imgData;

  changes.forEach((color, pointKey) => {
    const point = parsePointKey(pointKey);
    setPixelColor(data, { point, width, color });
  });
}

export function applyAlphaZero(imgData: ImageData, changes: PixelChangeMap): void {
  const { width, data } = imgData;

  changes.forEach((originalColor, pointKey) => {
    const point = parsePointKey(pointKey);
    const transparentColor = createTransparentColor(originalColor);
    setPixelColor(data, { point, width, color: transparentColor });
  });
}

// Brush erase functionality
export type BrushEraseParams = {
  imgData: ImageData;
  centerX: number;
  centerY: number;
  radiusX: number;
  radiusY: number;
  tolerance: number;
};

type PixelProcessParams = {
  sx: number;
  sy: number;
  centerX: number;
  centerY: number;
  radiusX: number;
  radiusY: number;
  processed: Uint8Array;
  alpha: Uint8ClampedArray;
  canvasWidth: number;
};

function shouldProcessPixel(params: PixelProcessParams): boolean {
  const { sx, sy, centerX, centerY, radiusX, radiusY, processed, alpha, canvasWidth } = params;
  const dx = sx - centerX;
  const ex = dx / radiusX;
  const dy = sy - centerY;
  const ey = dy / radiusY;

  // Check if outside ellipse
  if (ex * ex + ey * ey > 1) return false;

  const arrIdx = sy * canvasWidth + sx;
  // Check if region already erased
  if (processed[arrIdx]) return false;

  const dataIdx = arrIdx * 4;
  // Check if transparent already
  if (alpha[dataIdx + 3] === 0) return false;

  return true;
}

type BrushBounds = {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
};

function calculateBrushBounds(params: {
  centerX: number;
  centerY: number;
  radiusX: number;
  radiusY: number;
  width: number;
  height: number;
}): BrushBounds {
  const { centerX, centerY, radiusX, radiusY, width, height } = params;
  return {
    minX: Math.max(0, centerX - radiusX),
    maxX: Math.min(width - 1, centerX + radiusX),
    minY: Math.max(0, centerY - radiusY),
    maxY: Math.min(height - 1, centerY + radiusY)
  };
}

export function performBrushErase(params: BrushEraseParams): number {
  const { imgData, centerX, centerY, radiusX, radiusY, tolerance } = params;
  const { width, height, data } = imgData;

  const bounds = calculateBrushBounds({ centerX, centerY, radiusX, radiusY, width, height });
  const processed = new Uint8Array(width * height);
  let totalChanged = 0;

  for (let sy = bounds.minY; sy <= bounds.maxY; sy += 1) {
    for (let sx = bounds.minX; sx <= bounds.maxX; sx += 1) {
      if (
        shouldProcessPixel({
          sx,
          sy,
          centerX,
          centerY,
          radiusX,
          radiusY,
          processed,
          alpha: data,
          canvasWidth: width
        })
      ) {
        const changes = floodFillEraseWithDiff(imgData, {
          startX: sx,
          startY: sy,
          tolerance
        });
        if (changes.size > 0) {
          totalChanged += changes.size;
          // Mark processed pixels to avoid redundant fills
          for (const key of changes.keys()) {
            const point = parsePointKey(key);
            const idx = point.y * width + point.x;
            processed[idx] = 1;
          }
        } else {
          const arrIdx = sy * width + sx;
          processed[arrIdx] = 1;
        }
      }
    }
  }

  return totalChanged;
}

export function performManualBrushErase(params: BrushEraseParams): number {
  const { imgData, centerX, centerY, radiusX, radiusY } = params;
  const { width, height, data } = imgData;

  const bounds = calculateBrushBounds({ centerX, centerY, radiusX, radiusY, width, height });
  let totalChanged = 0;

  for (let sy = bounds.minY; sy <= bounds.maxY; sy += 1) {
    for (let sx = bounds.minX; sx <= bounds.maxX; sx += 1) {
      const dx = sx - centerX;
      const dy = sy - centerY;
      const normalizedDistance = (dx * dx) / (radiusX * radiusX) + (dy * dy) / (radiusY * radiusY);

      if (normalizedDistance <= 1) {
        const pixelIndex = getPixelIndex(sx, sy, width);
        const currentAlpha = data[pixelIndex + 3] ?? 0;

        if (currentAlpha > 0) {
          data[pixelIndex + 3] = TRANSPARENT_ALPHA;
          totalChanged += 1;
        }
      }
    }
  }

  return totalChanged;
}
